import {
  MessageType,
  MessageVisibility,
  TaskState,
} from "../data/patientApiSlice";

import { FilterType } from "../../screens/Patients";
import { ViewingMode } from "../appState/uxSlice";

export enum LogoutReason {
  UserInitiated = "UserInitiated",
  PatientGroupChanged = "PatientGroupChanged",
  ApiError = "ApiError",
  TokenExpired = "TokenExpired",
  UnauthorizedRole = "UnauthorizedRole",
}

export enum PaneType {
  TaskList = "TaskList",
  ChatUx = "ChatUx",
}

export enum SelectionSource {
  TaskList = "TaskList",
  PatientDetail = "PatientDetail",
  NavButton = "NavigationButton",
}

export interface FeedbackData {
  feedbackCategories: string[];
  isRelatedToPortal: boolean;
  portalId?: string;
  description: string;
  priority: string;
}

export enum FeedbackPriority {
  Urgent = "urgent",
  GeneralFeedback = "generalFeedback",
}

export interface FeedbackFormData {
  feedbackCategories: string[];
  isRelatedToPortal: boolean;
  portalId?: string;
  description: string;
  priority: FeedbackPriority;
}

export interface FeedbackLoggingPayload {
  feedback: FeedbackData;
  deviceType: string;
  userAgent: string;
  url: string;
  userName: string;
  userEmail: string;
  patientGroup?: string;
  patientId: string;
}

export interface ViewClientPhoneLoggingPayload {
  userName: string;
  reason: string;
  patientUid: string;
}

export type LogEventData = { group?: string } & (
  | {
      event: "userLogin";
      data: {
        success: boolean;
        errorMsg?: string;
      };
    }
  | {
      event: "userLogout";
      data: {
        reason: LogoutReason;
        extraData?: Record<string, unknown>;
      };
    }
  | {
      event: "paneOpened";
      data: {
        pane: PaneType;
      };
    }
  | {
      event: "groupByPatientSelected";
      data: {
        selected: boolean;
      };
    }
  | {
      event: "search";
      data: {
        hasSearchTerm: boolean;
      };
    }
  | {
      event: "filterButtonToggled";
      data: {
        filterType: FilterType;
        resultingFilters: FilterType[];
      };
    }
  | {
      event: "taskStateSelected";
      data: {
        state: TaskState;
      };
    }
  | {
      event: "taskSelected";
      data: {
        taskUid: string;
        selectionSource: SelectionSource;
      };
    }
  | {
      event: "patientDetailViewingModeChanged";
      data: {
        viewingMode: ViewingMode;
      };
    }
  | {
      event: "showOnlyRecentAndActiveSelected";
      data: {
        selected: boolean;
      };
    }
  | {
      event: "taskNoteAdded";
      data: {
        taskUid: string;
      };
    }
  | {
      event: "patientNoteAdded";
      data: {
        patientUid: string;
      };
    }
  | {
      event: "taskAdded";
      data: {
        taskUid: string;
      };
    }
  | {
      event: "taskCompleted";
      data: {
        taskUid: string;
        numActionsTaken: number;
        hasNotes: boolean;
      };
    }
  | {
      event: "taskDeleted";
      data: {
        taskUid: string;
        hasNotes: boolean;
      };
    }
  | {
      event: "taskFlagged";
      data: {
        taskUid: string;
        flagged: boolean;
      };
    }
  | {
      event: "clinicianChatResponse";
      data: {
        patientUid: string;
        messageType: MessageType;
      };
    }
  | {
      event: "markConversationAsRead";
      data: {
        patientUid: string;
      };
    }
  | {
      event: "markConversationAsUnread";
      data: {
        patientUid: string;
      };
    }
  | {
      event: "issueOccurred";
      data: {
        issueId: string;
        error: string;
        canRetry: boolean;
      };
    }
  | {
      event: "issueRetried";
      data: {
        issueId: string;
      };
    }
  | {
      event: "issueRemoved";
      data: {
        issueId: string;
      };
    }
  | {
      event: "messageVisibilityChanged";
      data: {
        patientUid: string;
        messageUid: string;
        visibility: MessageVisibility;
      };
    }
  | {
      event: "userFeedback";
      data: FeedbackLoggingPayload;
    }
  | {
      event: "viewClientPhoneNumber";
      data: ViewClientPhoneLoggingPayload;
    }
);
